"use client";
import React, { useRef, useEffect, useState, useCallback } from "react";
import useEmblaCarousel from 'embla-carousel-react';
import { motion, useAnimation, useInView } from "framer-motion";
import Autoplay from 'embla-carousel-autoplay';
import { FiArrowRight, FiArrowLeft, FiStar } from "react-icons/fi";
import electriverse from "../images/electriverse.png";
import solar from "../images/solar.png";

const PortfolioSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  
  // Enhanced carousel with navigation
  const autoplay = useRef(Autoplay({ delay: 5000, stopOnInteraction: true }));
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    align: "center",
    skipSnaps: false
  }, [autoplay.current]);
  
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  
  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);
  
  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [controls, isInView]);


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { 
        duration: 0.7, 
        ease: [0.23, 1, 0.320, 1] 
      },
    },
  };

  const scaleVariants = {
    hidden: { scale: 0.95, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.7,
        ease: [0.23, 1, 0.320, 1]
      }
    }
  };

const projects = [
  {
    title: "Electriverse – Revenue Rocket 🚀",
    description: "Transformed a struggling e-commerce business into a revenue powerhouse! Built with Laravel and MySQL, this high-performance platform now handles 10,000+ daily users and boosted client revenue by a staggering 40% in just Q2. Features include AI-powered product recommendations, real-time inventory management, and seamless payment processing.",
    tags: ["Laravel", "MySQL", "Stripe API", "AI Integration"],
    image: electriverse,
    dark: true,
    results: ["40% Revenue Increase", "10K+ Daily Users", "99.9% Uptime"]
  },
  {
    title: "3e Solar Ventures – Smart Energy Revolution ⚡",
    description: "Revolutionized the solar industry with an intelligent platform that's changing how people think about renewable energy. Features a dynamic quotation generator, engaging blog platform, and interactive savings calculator. The AI-powered system provides real-time analytics, helping users save up to 60% on energy costs while contributing to a greener planet.",
    tags: ["React", "Node.js", "TensorFlow.js", "MUI", "AI Analytics"],
    image: solar,
    dark: false,
    results: ["60% Energy Savings", "500+ Installations", "AI-Powered Insights"]
  },
  // {
  //   title: "FinTrack – Real-Time Financial Dashboard",
  //   description: "Enterprise-grade dashboard offering real-time financial analytics, budget monitoring, and forecasting tools. Helps companies track key metrics using rich data visualizations.",
  //   tags: ["Next.js", "D3.js", "Firebase"],
  //   image: "/fintrack.png",
  //   dark: true,
  //   url: "https://fintrack.example.com"
  // }
];

  return (
    <section className="relative py-16 md:py-20 lg:py-24 px-3 md:px-6 lg:px-0 overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-40 h-40 md:w-72 md:h-72 lg:w-96 lg:h-96 bg-gradient-to-br from-blue-100/20 to-transparent rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 left-0 w-40 h-40 md:w-72 md:h-72 lg:w-96 lg:h-96 bg-gradient-to-tr from-blue-100/20 to-transparent rounded-full blur-3xl -z-10"></div>

      <div className="max-w-7xl mx-auto w-full">
        <div className="flex flex-col gap-12 md:gap-16 lg:gap-20">
          {/* Header section */}
          <motion.div
            ref={ref}
            initial="hidden"
            animate={controls}
            variants={containerVariants}
            className="text-center"
          >
            <motion.div
              variants={itemVariants}
              className="inline-flex items-center justify-center gap-2 mb-4 md:mb-6"
            >
              <div className="h-1 w-6 md:w-8 bg-gradient-to-r from-blue-500 to-blue-600"></div>
              <span className="text-xs md:text-sm font-semibold text-blue-600 uppercase tracking-widest">Our Success Stories</span>
              <div className="h-1 w-6 md:w-8 bg-gradient-to-l from-blue-500 to-blue-600"></div>
            </motion.div>

            <motion.h2 
              variants={itemVariants}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-4 md:mb-6"
            >
              Projects That <span className="text-blue-600">Transform</span> <br /> Businesses
            </motion.h2>
            
            <motion.p 
              variants={itemVariants}
              className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed"
            >
              We don't just build digital products—we create strategic solutions that drive measurable growth, innovation, and lasting impact for our partners.
            </motion.p>
            
            <motion.div 
              variants={scaleVariants}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-6 mt-8 md:mt-16"
            >
              {[
                { value: "17+", label: "Projects Delivered", icon: "📊" },
                { value: "98%", label: "Client Retention Rate", icon: "⭐" },
                { value: "40%", label: "Avg ROI Growth", icon: "📈" },
              ].map((metric, index) => (
                <motion.div 
                  key={index}
                  whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(24, 44, 212, 0.1)" }}
                  className="group relative bg-white rounded-xl md:rounded-2xl border border-gray-100/50 hover:border-blue-200/50 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
                  style={{padding: '20px 16px md:32px md:24px'}}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="text-2xl md:text-3xl mb-2 md:mb-3">{metric.icon}</div>
                    <div className="text-2xl md:text-4xl font-bold text-blue-600 mb-1 md:mb-2">{metric.value}</div>
                    <div className="text-xs md:text-sm font-medium text-gray-700">{metric.label}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
          
          {/* Portfolio Carousel */}
          <motion.div 
            variants={scaleVariants}
            className="relative group"
          >
            <div className="overflow-hidden rounded-3xl" ref={emblaRef}>
              <div className="flex">
                {projects.map((project, index) => (
                  <div 
                    key={index} 
                    className="flex-[0_0_100%] min-w-0"
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <motion.div 
                      className="relative rounded-3xl overflow-hidden backdrop-blur-xl bg-gradient-to-br border border-white/20 shadow-2xl"
                      style={{
                        background: project.dark 
                          ? 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%)'
                          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)',
                      }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      {/* Animated gradient overlay */}
                      <motion.div 
                        className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"
                        animate={{ opacity: hoveredIndex === index ? 0.3 : 0.1 }}
                        transition={{ duration: 0.3 }}
                      />
                      
                      <div className="relative z-20 flex flex-col lg:flex-row items-start lg:items-center gap-6 md:gap-8 lg:gap-12 p-4 md:p-8 lg:p-16 h-full">
                        {/* Left Content */}
                        <motion.div 
                          className="flex-1 space-y-4 md:space-y-6 lg:space-y-8"
                          initial={{ x: -50, opacity: 0 }}
                          whileInView={{ x: 0, opacity: 1 }}
                          transition={{ duration: 0.6 }}
                        >
                          <div>
                            <div className="inline-flex items-center gap-2 mb-2 md:mb-4">
                              <span className="w-2 h-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"></span>
                              <span className={`text-xs font-semibold tracking-widest uppercase ${project.dark ? 'text-blue-300' : 'text-blue-600'}`}>
                                Featured Project
                              </span>
                            </div>
                            <h3 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-3 md:mb-6 ${project.dark ? 'text-white' : 'text-gray-900'}`}>
                              {project.title}
                            </h3>
                            <p className={`text-xs sm:text-sm md:text-base lg:text-lg leading-relaxed ${project.dark ? 'text-gray-300' : 'text-gray-600'}`}>
                              {project.description}
                            </p>
                          </div>
                          
                          {/* Tech Stack */}
                          <div>
                            <p className={`text-xs font-semibold uppercase tracking-widest mb-2 md:mb-4 ${project.dark ? 'text-gray-400' : 'text-gray-500'}`}>
                              Tech Stack
                            </p>
                            <div className="flex flex-wrap gap-2 md:gap-3">
                              {project.tags.map((tag, idx) => (
                                <motion.div
                                  key={idx}
                                  whileHover={{ scale: 1.05, y: -2 }}
                                  className={`rounded-full text-xs md:text-sm font-medium px-3 md:px-4 py-1.5 md:py-2 backdrop-blur-sm border transition-all ${
                                    project.dark 
                                      ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' 
                                      : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                                  }`}
                                >
                                  {tag}
                                </motion.div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Key Results */}
                          <div>
                            <p className={`text-xs font-semibold uppercase tracking-widest mb-2 md:mb-4 ${project.dark ? 'text-gray-400' : 'text-gray-500'}`}>
                              Key Results
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                              {project.results.map((result, idx) => (
                                <motion.div
                                  key={idx}
                                  whileHover={{ x: 6 }}
                                  className={`flex items-center gap-2 md:gap-3 p-2 md:p-3 rounded-lg md:rounded-xl backdrop-blur-sm border ${
                                    project.dark
                                      ? 'bg-emerald-500/10 border-emerald-500/30'
                                      : 'bg-emerald-50 border-emerald-200'
                                  }`}
                                >
                                  <span className="text-base md:text-lg">✨</span>
                                  <span className={`text-xs md:text-sm font-medium ${project.dark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                                    {result}
                                  </span>
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                        
                        {/* Right Image */}
                        <motion.div 
                          className="flex-1 w-full relative h-[250px] sm:h-[350px] md:h-[450px] lg:h-[600px] rounded-xl md:rounded-2xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-50"
                          initial={{ x: 50, opacity: 0 }}
                          whileInView={{ x: 0, opacity: 1 }}
                          transition={{ duration: 0.6 }}
                        >
                          <motion.div 
                            className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-transparent"
                            animate={{ opacity: hoveredIndex === index ? 0.2 : 0.1 }}
                            transition={{ duration: 0.3 }}
                          />
                          <motion.img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-full object-cover object-left-top rounded-2xl"
                            animate={{ scale: hoveredIndex === index ? 1.05 : 1 }}
                            transition={{ duration: 0.3 }}
                          />
                        </motion.div>
                      </div>
                    </motion.div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation Arrows */}
            <motion.button 
              onClick={scrollPrev}
              whileHover={{ scale: 1.1, x: -4 }}
              whileTap={{ scale: 0.95 }}
              className="absolute left-2 md:left-4 lg:left-8 top-1/2 -translate-y-1/2 z-40 bg-white rounded-full shadow-lg backdrop-blur-sm border border-gray-200 hover:border-blue-300 hover:shadow-xl opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-300"
              style={{padding: '10px'}}
              aria-label="Previous project"
            >
              <FiArrowLeft className="text-gray-700 text-lg md:text-xl" />
            </motion.button>
            
            <motion.button 
              onClick={scrollNext}
              whileHover={{ scale: 1.1, x: 4 }}
              whileTap={{ scale: 0.95 }}
              className="absolute right-2 md:right-4 lg:right-8 top-1/2 -translate-y-1/2 z-40 bg-white rounded-full shadow-lg backdrop-blur-sm border border-gray-200 hover:border-blue-300 hover:shadow-xl opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-300"
              style={{padding: '10px'}}
              aria-label="Next project"
            >
              <FiArrowRight className="text-gray-700 text-lg md:text-xl" />
            </motion.button>
            
            {/* Slide Indicators */}
            <motion.div 
              className="flex justify-center items-center gap-2 md:gap-3 mt-8 md:mt-12"
              variants={itemVariants}
            >
              <span className="text-xs md:text-sm text-gray-500 font-medium">
                {selectedIndex + 1} / {scrollSnaps.length}
              </span>
              <div className="flex gap-2">
                {scrollSnaps.map((_, index) => (
                  <motion.button
                    key={index}
                    onClick={() => emblaApi && emblaApi.scrollTo(index)}
                    className={`rounded-full transition-all ${
                      selectedIndex === index 
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    animate={{
                      width: selectedIndex === index ? 32 : 10,
                      height: 10
                    }}
                    whileHover={{ scale: 1.2 }}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
              <span className="text-xs md:text-sm text-gray-500 font-medium">
                {scrollSnaps.length} Projects
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;